import type { AgentPlatformOpPlanDraft, AgentPlatformOpPlanStep } from '../agent-draft.js';
import type { Actionable } from './actions.js';
import type { CardScheduleGrid } from './input-maps.js';
import type { AgentCardKind } from './index.js';
import type { OpCardRoute } from './routes.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * B · KAMPANYA KURMA KARTLARI — alan ajanı: kampanya (Faz 8 dalga 2)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Yedi tür, hepsi "kampanyayı kurarken karar verilen şey"in sohbetteki yüzü:
 *
 *   campaign_plan  işlem planı onay kartının (`platform_op_plan`) ÖZEL
 *                  görünümü — yapı ağacı (kampanya → set → reklam), bütçe/
 *                  takvim, hedefleme çipleri, reklam maketi, doğrulama.
 *                  🔴 Onay/ret/düzenleme AYNEN `agent-proposal.tsx` yolunda;
 *                  bu dosya yalnız planın OKUNUŞUNU türetiyor
 *                  (`campaignPlanOf`, saf). AgentCard dalı da aynı şekil:
 *                  kayıtlı bir kart (ör. ileride bir okuma işleminden) aynı
 *                  görünümle çizilir, karar yine onay kartında (`proposalId`).
 *   audience       tahmini kitle büyüklüğü + hedefleme çipleri; "kitle olarak
 *                  kaydet" (TikTok/LinkedIn kayıtlı kitle — Meta API'si
 *                  kayıtlı kitle OLUŞTURMUYOR, X'te karşılığı yok).
 *   keywords       Google anahtar kelime fikirleri: hacim, TBM aralığı,
 *                  rekabet; çoklu seçim + eşleme türü + negatifler.
 *                  Hedef reklam grubu biliniyorsa "seçilenleri ekle" DOĞRUDAN
 *                  platform işlemi (`google.search_term.add_as_keyword` —
 *                  tek `matchType`, `searchTerms: string[]` → `ids` girdisi
 *                  dönüştürücüsüz bağlanıyor); bilinmiyorsa cevap (reply).
 *   lead_form      form MAKETİ (sorular, gizlilik, teşekkür ekranı) — TANIM,
 *                  cevap değil. Liste okumasında birden çok form + "bunu kullan".
 *   schedule       7×24 gün-saat ısı haritası + açık saatler; düzenle →
 *                  platforma göre uygula (Meta/Google/TikTok platform işlemi,
 *                  `grid` + adlı dönüştürücü `./input-maps.ts`); LinkedIn ve
 *                  X'te gün bölme YOK → cevap ile Mentorivo saatli kuralı.
 *   ad_preview     platformun KENDİ önizlemesi (yerleşim sekmeleri); adres
 *                  kısa ömürlü → karta YAZILMAZ, canlı uçtan.
 *   picker         aramalı genel varlık seçici (Sayfa, piksel, kitle, fon
 *                  kaynağı, ürün seti, YouTube videosu…) — `ask_user`ın dört
 *                  şık sınırını aşan her "listeden seç". Seçim `reply` ile
 *                  modele döner; liste canlı uçtan tazelenebilir.
 *
 * 🔴 KİŞİSEL VERİ YOK — lead formu kartı formun TANIMINI taşır; seçici
 * işletme varlıklarını (Sayfa, piksel, kitle ADI) taşır, kişi değil. Kart
 * alan adları kemerin anahtar listesinden uzak tutuldu (`fields`, `users`,
 * `rows`, `address` gibi adlar YOK — `pii-seal.ts`).
 *
 * Üretici tarafı: `apps/api/src/agent/cards/campaign/`. Görünüm:
 * `apps/web/src/components/dashboard/agent/tool-renderers/campaign.tsx`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Ortak parçalar
// ═══════════════════════════════════════════════════════════════════════════

export type CampaignChipKind =
  | 'location'
  | 'age'
  | 'gender'
  | 'language'
  | 'interest'
  | 'behavior'
  | 'custom'
  | 'lookalike'
  | 'keyword'
  | 'placement'
  | 'device'
  | 'other';

/**
 * Hedefleme çipi. `label` platformun sözcüğü ya da adı (ülke kodu, ilgi alanı
 * adı, yaş aralığı "25–54"); cinsiyet `male`/`female`/`all` — ekran çevirir.
 */
export interface CampaignChip {
  kind: CampaignChipKind;
  label: string;
  exclude?: boolean;
}

export type CampaignBudget = { amount: number; period: 'daily' | 'lifetime' };

// ── campaign_plan ─────────────────────────────────────────────────────────

export interface CampaignPlanNode {
  /** `s<adım>` ya da iç içe çocukta `s<adım>.<yol>`. */
  id: string;
  parentId: string | null;
  level: 'campaign' | 'adset' | 'ad';
  platform: string;
  name: string;
  /** Bu düğümü OLUŞTURAN plan adımı (1'den) — düzenleme ona gider. */
  stepIndex: number | null;
  budget: CampaignBudget | null;
  objective: string | null;
  status: string | null;
}

/** Reklam maketi — `campaigns/ad-preview` havuz bileşeniyle çizilir. */
export interface CampaignPlanAd {
  id: string;
  platform: string;
  stepIndex: number | null;
  /** Kısa etiket — adım etiketi ya da reklam adı. */
  label: string;
  /** Mentorivo kreatif kimlikleri (görsel `byIds` ile ekranda çekilir). */
  creativeIds: string[];
  headline: string | null;
  body: string | null;
  cta: string | null;
  linkUrl: string | null;
}

export interface CampaignPlanValidation {
  status: 'passed' | 'failed' | 'skipped' | 'pending';
  issues: { code: string; level: 'block' | 'warn'; params?: Record<string, string | number> }[];
}

/** Planın okunuşu — `campaignPlanOf` üretir, AgentCard dalı da bu şekil. */
export interface CampaignPlanView {
  platforms: string[];
  objective: string | null;
  name: string | null;
  tree: CampaignPlanNode[];
  currency: string | null;
  /** Planın açtığı GÜNLÜK harcama (adımların `dailySpendAdded` toplamı) ya da ömür bütçesi. */
  budget: CampaignBudget | null;
  schedule: { start: string | null; end: string | null } | null;
  targeting: CampaignChip[];
  ads: CampaignPlanAd[];
  validation: CampaignPlanValidation | null;
}

// ── keywords ──────────────────────────────────────────────────────────────

export const KEYWORD_MATCH_TYPES = ['BROAD', 'PHRASE', 'EXACT'] as const;
export type KeywordMatchType = (typeof KEYWORD_MATCH_TYPES)[number];

export interface KeywordIdea {
  text: string;
  /** Aylık ortalama arama. */
  volume: number | null;
  /** Sayfa üstü teklif aralığı (ANA birim, hesabın para birimi). */
  cpcLow: number | null;
  cpcHigh: number | null;
  competition: 'LOW' | 'MEDIUM' | 'HIGH' | null;
  /** 0–100 rekabet endeksi. */
  competitionIndex: number | null;
}

// ── lead_form ─────────────────────────────────────────────────────────────

export interface LeadFormQuestion {
  /** Platformun soru türü (`EMAIL`, `FULL_NAME`, `CUSTOM`…) — ekran çevirir. */
  type: string;
  /** Özel sorunun metni (formun TANIMI, cevap değil) — düz metin çizilir. */
  label: string | null;
  required: boolean;
  /** Çoktan seçmeli sorunun şık sayısı. */
  choices: number;
}

export interface LeadFormSpec {
  formRef: string;
  name: string | null;
  locale: string | null;
  status: string | null;
  intro: { headline: string | null; body: string | null } | null;
  questions: LeadFormQuestion[];
  privacyUrl: string | null;
  thankYou: { headline: string | null; body: string | null; url: string | null } | null;
}

// ── picker ────────────────────────────────────────────────────────────────

export const PICKER_ENTITIES = [
  'page',
  'instagram',
  'pixel',
  'conversion',
  'lead_form',
  'audience',
  'saved_audience',
  'interest',
  'location',
  'funding',
  'catalog',
  'product_set',
  'youtube_video',
  'image',
  'merchant',
  'identity',
  'organization',
  'other',
] as const;
export type PickerEntity = (typeof PICKER_ENTITIES)[number];

export interface PickerItem {
  /** Modele dönecek değer — platform kimliği / kaynak adı. */
  id: string;
  label: string;
  /** İkinci satır — tür, boyut, son tetiklenme… (işletme verisi). */
  sub: string | null;
  /** `ok` kullanılabilir · `warn` dikkat (küçük kitle, pasif) · `off` kullanılamaz. */
  tone: 'ok' | 'warn' | 'off' | null;
  /** Durum sözcüğü (platformun) — ekran `cards.picker.status.<x>` ile çevirir. */
  status: string | null;
  /** Yalnız KALICI, herkese açık küçük resim (YouTube `i.ytimg.com`); imzalı adres YOK. */
  thumb: string | null;
}

// ── schedule ──────────────────────────────────────────────────────────────

export const SCHEDULE_DAYS = 7;
export const SCHEDULE_HOURS = 24;

// ═══════════════════════════════════════════════════════════════════════════
// Birlik
// ═══════════════════════════════════════════════════════════════════════════

export type CampaignAgentCard =
  /** KAMPANYA PLANI — onay kartının görünüm katmanı (karar `proposalId` ucunda). */
  | Actionable<
      {
        kind: 'campaign_plan';
        proposalId: string | null;
      } & CampaignPlanView
    >
  /** KİTLE — hedefleme çipleri, tahmini büyüklük, "kitle olarak kaydet". */
  | Actionable<{
      kind: 'audience';
      platform: string;
      name: string | null;
      chips: CampaignChip[];
      size: { lower: number | null; upper: number | null } | null;
      /** Platform tahmini henüz hazır değil (Meta `ready: false`). */
      estimating: boolean;
      /** Kayıtlı kitle oluşturulabiliyor mu (TikTok, LinkedIn) — eylem de onu söyler. */
      canSave: boolean;
      /** Kaydedildiyse platformdaki kitle referansı. */
      savedRef: string | null;
    }>
  /**
   * ANAHTAR KELİME FİKİRLERİ — hacim/TBM/rekabet; seçim + eşleme türü +
   * negatifler → "seçilenleri ekle" (`input: 'ids'`, `choices` = fikir metinleri).
   */
  | Actionable<{
      kind: 'keywords';
      platform: string;
      currency: string | null;
      language: string | null;
      geo: string[];
      seeds: string[];
      ideas: KeywordIdea[];
      /** Önerilen negatif kelimeler (model ya da arama ekranı). */
      negatives: string[];
      /** Varsayılan eşleme türü — ekran bununla açılır. */
      matchType: KeywordMatchType;
      target: { campaignRef: string | null; adGroupRef: string | null; name: string | null } | null;
      total: number;
    }>
  /** LEAD FORMU MAKETİ — tanım (soru TÜRLERİ), cevap değil. */
  | Actionable<{
      kind: 'lead_form';
      platform: string;
      forms: LeadFormSpec[];
      total: number;
    }>
  /**
   * GÜN-SAAT TAKVİMİ — 7×24 ısı haritası (`heat`, 0–1) + açık saatler
   * (`enabled`, 0/1; `null` = kısıt yok / okunamadı). Düzenle/uygula:
   * `input: 'grid'`.
   */
  | Actionable<{
      kind: 'schedule';
      platform: string;
      timezone: string | null;
      /** Isının metriği (`clicks`, `conversions`…); `heat` yoksa `null`. */
      metric: string | null;
      heat: number[][] | null;
      enabled: (0 | 1)[][] | null;
      /** Modelin önerdiği ızgara (ısıdan türetilmiş) — ekran düzenlemeye bununla başlar. */
      proposed: (0 | 1)[][] | null;
      target: { level: 'campaign' | 'adset'; ref: string | null; name: string | null } | null;
      /** `platform` = platform işlemiyle; `rule` = Mentorivo saatli kuralıyla (LinkedIn, X). */
      apply: 'platform' | 'rule';
      /** Platformun koşulu — Meta gün bölme ÖMÜR bütçesi ister. */
      constraint: 'meta_lifetime_budget' | null;
    }>
  /**
   * PLATFORM ÖNİZLEMESİ — yerleşim sekmeleri. Önizleme gövdesi (iframe/görsel
   * adresi) KISA ÖMÜRLÜ: canlı uçtan (`CampaignCardLiveData['ad_preview']`).
   */
  | Actionable<{
      kind: 'ad_preview';
      platform: string;
      adRef: string | null;
      creativeId: string | null;
      placements: { key: string; label: string | null }[];
      /** Canlı kaynağın yeniden okuyacağı işlem — parametrelerde sır yok. */
      source: { opId: string; params: Record<string, unknown> } | null;
    }>
  /** SEÇİCİ — aramalı liste; seçim `reply` eylemiyle modele döner. */
  | Actionable<{
      kind: 'picker';
      platform: string;
      entity: PickerEntity;
      multiple: boolean;
      items: PickerItem[];
      total: number;
      /** Platformda listelenenden fazlası var. */
      truncated: boolean;
      source: { opId: string; params: Record<string, unknown> } | null;
    }>;

export const CAMPAIGN_CARD_KINDS = [
  'campaign_plan',
  'audience',
  'keywords',
  'lead_form',
  'schedule',
  'ad_preview',
  'picker',
] as const;

/** Çekirdek araç → tür (araç sonucu `ui`). */
export const CAMPAIGN_TOOL_CARDS: Readonly<Record<string, readonly AgentCardKind[]>> = {
  keyword_ideas: ['keywords'],
  ad_schedule: ['schedule'],
};

/**
 * Katalog OKUMA işlemi → tür. API üreticisi AYNI desenle
 * (`apps/api/src/agent/cards/campaign/index.ts`, `cards.spec.ts` karşılaştırıyor).
 */
export const CAMPAIGN_OP_CARDS: readonly OpCardRoute<AgentCardKind>[] = [
  // ── kitle tahmini ──
  { match: 'meta.targeting.reach_estimate', kind: 'audience' },
  { match: 'tiktok.targeting.estimate_size', kind: 'audience' },
  { match: 'linkedin.audience.count', kind: 'audience' },
  { match: 'x.audience.estimate', kind: 'audience' },
  // ── anahtar kelime fikirleri ──
  { match: 'google.keyword_plan.ideas', kind: 'keywords' },
  { match: 'google.keyword_plan.historical', kind: 'keywords' },
  // ── lead formu ──
  { match: 'meta.leadform.list', kind: 'lead_form' },
  { match: 'linkedin.leadform.list', kind: 'lead_form' },
  { match: 'linkedin.leadform.get', kind: 'lead_form' },
  { match: 'x.leadform.list', kind: 'lead_form' },
  { match: 'x.leadform.get', kind: 'lead_form' },
  { match: 'tiktok.leadform.list', kind: 'lead_form' },
  // ── platform önizlemesi ──
  { match: 'meta.preview.get', kind: 'ad_preview' },
  { match: 'google.ad.preview', kind: 'ad_preview' },
  { match: 'tiktok.creative.preview', kind: 'ad_preview' },
  { match: 'linkedin.ad.preview', kind: 'ad_preview' },
  { match: 'linkedin.ad.live_preview', kind: 'ad_preview' },
  { match: 'x.post.preview', kind: 'ad_preview' },
  // ── seçici (listeden seç) ──
  { match: 'meta.account.list_identities', kind: 'picker' },
  { match: 'meta.pixel.list', kind: 'picker' },
  { match: 'meta.custom_conversion.list', kind: 'picker' },
  { match: 'meta.audience.list', kind: 'picker' },
  { match: 'meta.saved_audience.list', kind: 'picker' },
  { match: 'meta.catalog.list', kind: 'picker' },
  { match: 'meta.product_set.list', kind: 'picker' },
  { match: 'meta.targeting.search', kind: 'picker' },
  { match: 'google.asset.list', kind: 'picker' },
  { match: 'google.merchant.list_links', kind: 'picker' },
  { match: 'google.audience.search_segments', kind: 'picker' },
  { match: 'google.geo.suggest', kind: 'picker' },
  { match: 'tiktok.identity.list', kind: 'picker' },
  { match: 'tiktok.pixel.list', kind: 'picker' },
  { match: 'tiktok.audience.list', kind: 'picker' },
  { match: 'tiktok.catalog.list', kind: 'picker' },
  { match: 'tiktok.catalog.product_set.list', kind: 'picker' },
  { match: 'linkedin.segment.list', kind: 'picker' },
  { match: 'linkedin.audience_template.list', kind: 'picker' },
  { match: 'linkedin.org.list_administered', kind: 'picker' },
  { match: 'linkedin.targeting.search', kind: 'picker' },
  { match: 'x.funding.list', kind: 'picker' },
  { match: 'x.audience.list', kind: 'picker' },
  { match: 'x.catalog.list', kind: 'picker' },
  { match: 'x.promotable_users.list', kind: 'picker' },
];

/** Canlı veri şekilleri — kaydedilmez, rol denetimli uçtan. */
export interface CampaignCardLiveData {
  ad_preview: {
    items: {
      placement: string;
      /** Platformun önizleme adresi (iframe, alan adı süzgecinden geçmiş) — kısa ömürlü. */
      iframeUrl: string | null;
      /** Çerçevelenemeyen önizleme — yeni sekmede açılır (Google, TikTok). */
      linkUrl: string | null;
      width: number | null;
      height: number | null;
    }[];
  };
  picker: {
    items: PickerItem[];
    total: number;
    truncated: boolean;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Takvim ızgarası yardımcıları (saf — kart, üretici ve ekran ortak)
// ═══════════════════════════════════════════════════════════════════════════

/** Boş (hepsi kapalı) ya da dolu (hepsi açık) 7×24 ızgara. */
export function scheduleGridFilled(value: 0 | 1): (0 | 1)[][] {
  return Array.from({ length: SCHEDULE_DAYS }, () => Array.from({ length: SCHEDULE_HOURS }, () => value));
}

/** TikTok 336 karakterlik gün bölme dizesi → ızgara (yarım saatlerden biri açıksa saat açık). */
export function scheduleGridOfTikTok(value: unknown): (0 | 1)[][] | null {
  if (typeof value !== 'string' || !/^[01]{336}$/.test(value)) return null;
  return Array.from({ length: SCHEDULE_DAYS }, (_, day) =>
    Array.from({ length: SCHEDULE_HOURS }, (_, hour) => {
      const at = day * 48 + hour * 2;
      return value[at] === '1' || value[at + 1] === '1' ? 1 : 0;
    }),
  );
}

const GOOGLE_WEEKDAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const GOOGLE_MINUTE: Record<string, number> = { ZERO: 0, FIFTEEN: 15, THIRTY: 30, FORTY_FIVE: 45 };

/**
 * Google `campaign_criterion.ad_schedule` satırları → ızgara. Satır yoksa
 * `null` (Google'da kısıt yok = her saat açık; ekran "kısıt yok" der).
 * Çeyrek saatli aralık, dokunduğu her saati açar.
 */
export function scheduleGridOfGoogle(
  slots: readonly { day: string; startHour: number; startMinute: string | number; endHour: number; endMinute: string | number }[],
): (0 | 1)[][] | null {
  if (slots.length === 0) return null;
  const grid = scheduleGridFilled(0);
  for (const slot of slots) {
    const day = GOOGLE_WEEKDAYS.indexOf(slot.day);
    if (day < 0) continue;
    const startMinute = typeof slot.startMinute === 'number' ? slot.startMinute : (GOOGLE_MINUTE[slot.startMinute] ?? 0);
    const endMinute = typeof slot.endMinute === 'number' ? slot.endMinute : (GOOGLE_MINUTE[slot.endMinute] ?? 0);
    const from = slot.startHour * 60 + startMinute;
    const to = slot.endHour * 60 + endMinute;
    for (let hour = 0; hour < SCHEDULE_HOURS; hour += 1) {
      if (hour * 60 < to && (hour + 1) * 60 > from) grid[day]![hour] = 1;
    }
  }
  return grid;
}

/** Izgaradaki açık saat sayısı (0–168). */
export function scheduleHoursOn(grid: CardScheduleGrid | null): number {
  if (!grid) return SCHEDULE_DAYS * SCHEDULE_HOURS;
  return grid.reduce((sum, row) => sum + row.reduce<number>((acc, cell) => acc + cell, 0), 0);
}

/**
 * Izgaranın kısa İNGİLİZCE özeti — kural modunda (LinkedIn/X) modele cevap
 * olarak gider: "Mon-Fri 09:00-18:00; Sat 10:00-14:00". Aynı saatleri
 * paylaşan ardışık günler birleşir. ≤400 karakter (reply sınırı).
 */
export function describeScheduleGrid(grid: CardScheduleGrid): string {
  const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dayText = grid.map((row) => {
    const runs: string[] = [];
    let start: number | null = null;
    for (let hour = 0; hour <= SCHEDULE_HOURS; hour += 1) {
      const on = hour < SCHEDULE_HOURS && row[hour] === 1;
      if (on && start === null) start = hour;
      if (!on && start !== null) {
        runs.push(`${String(start).padStart(2, '0')}:00-${String(hour).padStart(2, '0')}:00`);
        start = null;
      }
    }
    return runs.join(', ');
  });
  const parts: string[] = [];
  let from = 0;
  for (let day = 1; day <= SCHEDULE_DAYS; day += 1) {
    if (day < SCHEDULE_DAYS && dayText[day] === dayText[from]) continue;
    const text = dayText[from] || 'off';
    const span = day - 1 === from ? names[from] : `${names[from]}-${names[day - 1]}`;
    parts.push(`${span} ${text}`);
    from = day;
  }
  const out = parts.join('; ');
  return out.length > 400 ? `${out.slice(0, 399)}…` : out;
}

// ═══════════════════════════════════════════════════════════════════════════
// İşlem planı → kampanya planı okunuşu (`campaign_plan`)
// ═══════════════════════════════════════════════════════════════════════════

type Rec = Record<string, unknown>;

const isRec = (value: unknown): value is Rec => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const str = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
const num = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
};

/** Adımın düğüm düzeyi — yalnız OLUŞTURAN işlemler ağaca girer. */
function nodeLevel(step: Pick<AgentPlatformOpPlanStep, 'opId' | 'level'>): CampaignPlanNode['level'] | null {
  const verb = step.opId.split('.').pop() ?? '';
  if (!/^(create|launch|create_[a-z_]+)$/.test(verb)) return null;
  if (step.level === 'CAMPAIGN') return 'campaign';
  if (step.level === 'ADSET') return 'adset';
  if (step.level === 'AD') return 'ad';
  return null;
}

/** Nesnede anahtarı (büyük/küçük harf, alt çizgi bağımsız) arar — derinlik sınırlı. */
function pick(record: Rec, names: readonly string[]): unknown {
  const wanted = new Set(names.map((name) => name.toLowerCase().replace(/_/g, '')));
  for (const [key, value] of Object.entries(record)) {
    if (wanted.has(key.toLowerCase().replace(/_/g, '')) && value !== undefined && value !== null) return value;
  }
  return undefined;
}

function budgetOf(params: Rec, step: AgentPlatformOpPlanStep | null): CampaignBudget | null {
  const budget = pick(params, ['budget']);
  const daily =
    num(pick(params, ['dailyBudget', 'daily_budget', 'dailyBudgetAmount'])) ??
    (isRec(budget) ? num(pick(budget, ['daily', 'dailyBudget', 'amount'])) : null);
  if (daily !== null && daily > 0) return { amount: daily, period: 'daily' };
  const lifetime =
    num(pick(params, ['lifetimeBudget', 'lifetime_budget', 'totalBudget', 'total_budget'])) ??
    (isRec(budget) ? num(pick(budget, ['total', 'lifetime', 'lifetimeBudget'])) : null);
  if (lifetime !== null && lifetime > 0) return { amount: lifetime, period: 'lifetime' };
  if (typeof budget === 'number' && budget > 0) {
    const mode = String(pick(params, ['budgetMode', 'budget_mode']) ?? '').toUpperCase();
    return { amount: budget, period: mode.includes('TOTAL') || mode.includes('LIFETIME') ? 'lifetime' : 'daily' };
  }
  const spend = step?.policy?.facts?.dailySpendAdded;
  return typeof spend === 'number' && spend > 0 ? { amount: spend, period: 'daily' } : null;
}

function scheduleOf(params: Rec): { start: string | null; end: string | null } | null {
  const nested = pick(params, ['schedule', 'runSchedule']);
  const holder = isRec(nested) ? nested : params;
  const start = str(pick(holder, ['startTime', 'start_time', 'startDate', 'start_date', 'start'])) ?? str(pick(params, ['startTime', 'startDate']));
  const end = str(pick(holder, ['endTime', 'end_time', 'endDate', 'end_date', 'end'])) ?? str(pick(params, ['endTime', 'endDate']));
  return start || end ? { start, end } : null;
}

const CHIP_KEYS: readonly { test: RegExp; kind: CampaignChipKind }[] = [
  { test: /^(countries|countrycodes?|country)$/, kind: 'location' },
  { test: /^(cities|regions|zips|geomarkets|locationids|locations|geotargetconstants?|include)$/, kind: 'location' },
  { test: /^(interests?|interestcategoryids|interestkeywordids|userinterestids|topics?|topicids)$/, kind: 'interest' },
  { test: /^(behaviors?|lifeevents|industries|jobfunctions|seniorities|titles|skills)$/, kind: 'behavior' },
  { test: /lookalike/, kind: 'lookalike' },
  { test: /^(customaudiences|audienceids|audiences|userlistrefs|customaudiencerefs|matchedaudiences|savedaudienceid)$/, kind: 'custom' },
  { test: /^(keywords|searchthemes)$/, kind: 'keyword' },
  { test: /^(languages?|locales?)$/, kind: 'language' },
  { test: /^(placements|publisherplatforms|positions|facebookpositions|instagrampositions|channels)$/, kind: 'placement' },
  { test: /^(deviceplatforms|devices|useros|osversion|devicemodels)$/, kind: 'device' },
];

/** Hedefleme çipleri — adım parametrelerinden (ağaçtaki set ve kampanya adımları). */
function chipsOf(params: Rec, out: CampaignChip[], depth = 0, exclude = false): void {
  if (depth > 4 || out.length >= 40) return;
  const ageMin = num(pick(params, ['ageMin', 'age_min']));
  const ageMax = num(pick(params, ['ageMax', 'age_max']));
  if (ageMin !== null || ageMax !== null) {
    out.push({ kind: 'age', label: `${ageMin ?? 18}–${ageMax ?? 65}` });
  }
  for (const [key, value] of Object.entries(params)) {
    const norm = key.toLowerCase().replace(/_/g, '');
    const excluded = exclude || norm.startsWith('exclu');
    const base = norm.replace(/^excluded?/, '');
    if (norm === 'genders' || norm === 'gender') {
      for (const item of Array.isArray(value) ? value : [value]) {
        const label = typeof item === 'string' ? item.toLowerCase() : null;
        if (label) out.push({ kind: 'gender', label });
      }
      continue;
    }
    if (norm === 'agegroups' || norm === 'ageranges') {
      for (const item of Array.isArray(value) ? value : []) {
        if (typeof item === 'string') out.push({ kind: 'age', label: item.replace(/^AGE(_RANGE)?_/, '').replace(/_/g, '–') });
      }
      continue;
    }
    const rule = CHIP_KEYS.find((candidate) => candidate.test.test(base));
    if (rule) {
      for (const item of Array.isArray(value) ? value : [value]) {
        const label = chipLabel(item);
        if (label) out.push({ kind: rule.kind, label, ...(excluded ? { exclude: true } : {}) });
      }
      continue;
    }
    if (isRec(value)) chipsOf(value, out, depth + 1, excluded);
    else if (Array.isArray(value) && (norm === 'detailed' || norm === 'flexiblespec' || norm === 'targetingcriteria')) {
      for (const item of value) if (isRec(item)) chipsOf(item, out, depth + 1, excluded);
    }
  }
}

/**
 * Hedefleme nesnesinden çipler (anahtar adıyla sezgisel; tekrarsız, ≤30) —
 * kitle kartı ve plan okunuşu ortak. Okunamayan alan çipte yok.
 */
export function targetingChipsOf(params: Record<string, unknown>): CampaignChip[] {
  const out: CampaignChip[] = [];
  chipsOf(params, out);
  const seen = new Set<string>();
  return out
    .filter((chip) => {
      const key = `${chip.kind}:${chip.label}:${chip.exclude ? 1 : 0}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 30);
}

function chipLabel(item: unknown): string | null {
  if (typeof item === 'string') return item.trim().slice(0, 80) || null;
  if (typeof item === 'number') return String(item);
  if (!isRec(item)) return null;
  const label =
    str(item.name) ??
    str(item.text) ??
    str(item.country) ??
    str(item.key) ??
    str(item.geoTargetConstant) ??
    str(item.id) ??
    str(item.urn);
  return label ? label.slice(0, 80) : null;
}

const CREATIVE_KEYS = ['creativeId', 'creativeIds', 'creative_id'];

function creativeIdsOf(value: unknown, out: Set<string>, depth = 0): void {
  if (depth > 5 || out.size >= 6) return;
  if (Array.isArray(value)) {
    for (const item of value) creativeIdsOf(item, out, depth + 1);
    return;
  }
  if (!isRec(value)) return;
  for (const [key, item] of Object.entries(value)) {
    if (CREATIVE_KEYS.includes(key)) {
      for (const id of Array.isArray(item) ? item : [item]) {
        if (typeof id === 'string' && /^[0-9a-f-]{36}$/i.test(id)) out.add(id);
      }
    } else if (typeof item === 'object') creativeIdsOf(item, out, depth + 1);
  }
}

function firstText(value: unknown): string | null {
  if (typeof value === 'string') return str(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = firstText(item);
      if (text) return text;
    }
    return null;
  }
  if (isRec(value)) return str(value.text) ?? str(value.value);
  return null;
}

/** Reklamın metin parçaları — iç içe `creative`/`post`/`ad` nesnelerine de bakar. */
function adTextOf(params: Rec, depth = 0): Pick<CampaignPlanAd, 'headline' | 'body' | 'cta' | 'linkUrl'> {
  const out = {
    headline: firstText(pick(params, ['headline', 'headlines', 'title', 'cardTitle'])),
    body: firstText(pick(params, ['bodyText', 'body', 'message', 'primaryText', 'text', 'adText', 'commentary', 'descriptions', 'description'])),
    cta: (() => {
      const raw = pick(params, ['ctaType', 'cta', 'callToAction', 'callToActionType']);
      return isRec(raw) ? str(raw.type) : str(raw);
    })(),
    linkUrl: firstText(pick(params, ['linkUrl', 'link', 'finalUrl', 'finalUrls', 'landingPageUrl', 'landingPage', 'url'])),
  };
  if (depth < 3) {
    for (const key of ['creative', 'post', 'ad', 'objectStorySpec', 'object_story_spec', 'linkData', 'link_data', 'textAd']) {
      const nested = pick(params, [key]);
      if (!isRec(nested)) continue;
      const inner = adTextOf(nested, depth + 1);
      out.headline ??= inner.headline;
      out.body ??= inner.body;
      out.cta ??= inner.cta;
      out.linkUrl ??= inner.linkUrl;
    }
  }
  return out;
}

const CHILD_GROUP_KEYS = ['adGroups', 'adgroups', 'adSets', 'adsets', 'ad_sets', 'lineItems'];
const CHILD_AD_KEYS = ['ads', 'creatives'];

/**
 * İŞLEM PLANININ KAMPANYA OKUNUŞU — saf, sunucu ve ekran ortak.
 *
 * Ağaç yalnız OLUŞTURAN adımlardan (`create`/`launch` fiilleri, CAMPAIGN/
 * ADSET/AD düzeyi) ve tek istekte çocuk taşıyan oluşturmaların iç içe
 * listelerinden (`google.campaign.create` → `adGroups[].ads[]`,
 * `pmax.assetGroups[]`) kuruluyor. Ebeveyn adımın REFERANSINDAN (`$1…`);
 * referans yoksa aynı platformun son kampanyası. Oluşturma yoksa `null` —
 * kart genel adım listesine (`PlanDetails`) düşer.
 *
 * Çipler ve metinler anahtar ADI üzerinden sezgisel okunuyor: beş platformun
 * yüzlerce şeması tek tek eşlenmedi (sözleşme-vs-gerçeklik dersi — elle kopya
 * liste eskir). Okunamayan alan çipte yok; parametrelerin tamamı kartın
 * düzenleme gövdesinde duruyor.
 */
export function campaignPlanOf(draft: AgentPlatformOpPlanDraft): CampaignPlanView | null {
  if (draft.kind !== 'plan' || !Array.isArray(draft.steps)) return null;
  const tree: CampaignPlanNode[] = [];
  const ads: CampaignPlanAd[] = [];
  const chips: CampaignChip[] = [];
  const stepNode = new Map<number, CampaignPlanNode>();
  let schedule: { start: string | null; end: string | null } | null = null;
  let objective: string | null = null;

  const parentFor = (step: AgentPlatformOpPlanStep, want: CampaignPlanNode['level'][]): string | null => {
    for (const dep of [...(step.dependsOn ?? [])].reverse()) {
      const node = stepNode.get(dep);
      if (node && want.includes(node.level)) return node.id;
    }
    for (const node of [...tree].reverse()) {
      if (node.platform === step.platform && want.includes(node.level)) return node.id;
    }
    return null;
  };

  for (const step of draft.steps) {
    const level = nodeLevel(step);
    if (!level) continue;
    const params: Rec = isRec(step.input) ? step.input : {};
    const name =
      str(pick(params, ['name', 'campaignName', 'adGroupName'])) ?? step.label ?? (step.title?.en || step.opId);
    const node: CampaignPlanNode = {
      id: `s${step.index}`,
      parentId: level === 'campaign' ? null : parentFor(step, level === 'adset' ? ['campaign'] : ['adset', 'campaign']),
      level,
      platform: step.platform,
      name,
      stepIndex: step.index,
      budget: level === 'ad' ? null : budgetOf(params, step),
      objective: str(pick(params, ['objective', 'objectiveType', 'type'])),
      status: str(pick(params, ['status'])),
    };
    tree.push(node);
    stepNode.set(step.index, node);
    if (level === 'campaign') objective ??= node.objective;
    schedule ??= scheduleOf(params);

    if (level !== 'ad') {
      const targeting = pick(params, ['targeting', 'targetingCriteria', 'geo']);
      chipsOf(isRec(targeting) ? { targeting } : params, chips);
      if (isRec(targeting)) chipsOf(params, chips);
    }
    if (level === 'ad') ads.push(adOf(`s${step.index}`, step, params, name));

    /* Tek istekte çocuk taşıyan oluşturma (Google/TikTok ağaç kurucuları). */
    for (const key of CHILD_GROUP_KEYS) {
      const list = params[key];
      if (!Array.isArray(list)) continue;
      list.slice(0, 20).forEach((group, groupIndex) => {
        if (!isRec(group)) return;
        const groupNode: CampaignPlanNode = {
          id: `s${step.index}.${key}.${groupIndex}`,
          parentId: node.id,
          level: 'adset',
          platform: step.platform,
          name: str(group.name) ?? `${key} ${groupIndex + 1}`,
          stepIndex: step.index,
          budget: budgetOf(group, null),
          objective: null,
          status: str(group.status),
        };
        tree.push(groupNode);
        chipsOf(group, chips);
        for (const adKey of CHILD_AD_KEYS) {
          const children = group[adKey];
          if (!Array.isArray(children)) continue;
          children.slice(0, 10).forEach((child, adIndex) => {
            if (!isRec(child)) return;
            const adNode: CampaignPlanNode = {
              id: `${groupNode.id}.${adKey}.${adIndex}`,
              parentId: groupNode.id,
              level: 'ad',
              platform: step.platform,
              name: str(child.name) ?? str(child.format) ?? `${groupNode.name} · ${adIndex + 1}`,
              stepIndex: step.index,
              budget: null,
              objective: null,
              status: str(child.status),
            };
            tree.push(adNode);
            ads.push(adOf(adNode.id, step, child, adNode.name));
          });
        }
      });
    }
    /* Google PMax: `pmax.assetGroups` ayrıca (anahtar kampanyanın altında). */
    const pmax = pick(params, ['pmax']);
    if (isRec(pmax) && Array.isArray(pmax.assetGroups)) {
      pmax.assetGroups.slice(0, 20).forEach((group, groupIndex) => {
        if (!isRec(group)) return;
        const groupNode: CampaignPlanNode = {
          id: `s${step.index}.pmax.${groupIndex}`,
          parentId: node.id,
          level: 'adset',
          platform: step.platform,
          name: str(group.name) ?? `Asset group ${groupIndex + 1}`,
          stepIndex: step.index,
          budget: null,
          objective: null,
          status: null,
        };
        tree.push(groupNode);
        ads.push(adOf(groupNode.id, step, group, groupNode.name));
      });
    }
  }

  if (tree.length === 0) return null;

  /* Doğrulama — adımların platform ön doğrulaması. */
  const statuses = draft.steps.map((step) => step.validation?.status ?? 'skipped');
  const issues: CampaignPlanValidation['issues'] = [];
  draft.steps.forEach((step) => {
    const validation = step.validation;
    if (validation?.status === 'rejected') issues.push({ code: 'rejected', level: 'block', params: { step: step.index } });
    if (validation?.status === 'passed' && validation.warnings.length > 0) {
      issues.push({ code: 'warnings', level: 'warn', params: { step: step.index, count: validation.warnings.length } });
    }
  });
  const validation: CampaignPlanValidation = {
    status: statuses.includes('rejected')
      ? 'failed'
      : draft.steps.some((step) => step.validation?.status === 'skipped' && step.validation.reason === 'DEPENDS_ON_PREVIOUS')
        ? 'pending'
        : statuses.every((status) => status === 'passed')
          ? 'passed'
          : statuses.includes('passed')
            ? 'pending'
            : 'skipped',
    issues,
  };

  /* Bütçe: kampanya/set düğümlerinin günlük toplamı; yoksa politikanın saydığı harcama. */
  const dailyNodes = tree.filter((node) => node.budget?.period === 'daily' && node.level !== 'ad');
  const campaignDaily = dailyNodes.filter((node) => node.level === 'campaign');
  const counted = campaignDaily.length > 0 ? campaignDaily : dailyNodes;
  const dailyTotal = counted.reduce((sum, node) => sum + (node.budget?.amount ?? 0), 0);
  const lifetime = tree.find((node) => node.budget?.period === 'lifetime')?.budget ?? null;
  const policyDaily = draft.policy?.facts?.dailySpendAdded;
  const budget: CampaignBudget | null =
    dailyTotal > 0
      ? { amount: Math.round(dailyTotal * 100) / 100, period: 'daily' }
      : lifetime ?? (typeof policyDaily === 'number' && policyDaily > 0 ? { amount: policyDaily, period: 'daily' } : null);

  const seen = new Set<string>();
  const targeting = chips.filter((chip) => {
    const key = `${chip.kind}:${chip.label}:${chip.exclude ? 1 : 0}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const firstCampaign = tree.find((node) => node.level === 'campaign') ?? null;
  return {
    platforms: [...new Set(tree.map((node) => node.platform))],
    objective,
    name: firstCampaign?.name ?? draft.title ?? null,
    tree,
    currency: draft.steps.find((step) => step.currency)?.currency ?? null,
    budget,
    schedule,
    targeting: targeting.slice(0, 30),
    ads: ads.slice(0, 8),
    validation,
  };
}

function adOf(id: string, step: AgentPlatformOpPlanStep, params: Rec, label: string): CampaignPlanAd {
  const ids = new Set<string>();
  creativeIdsOf(params, ids);
  return {
    id,
    platform: step.platform,
    stepIndex: step.index,
    label,
    creativeIds: [...ids],
    ...adTextOf(params),
  };
}
